/**
 * Component for embedding Grafana dashboards or panels
 *
 * MULTI-TENANT: Uses signed URLs from the API to ensure tenant isolation.
 * The URL is refreshed before expiry to maintain continuous access.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSignedDashboardUrl,
  getGrafanaPanelUrl,
  getSystemTheme,
  getRefreshDelay,
  GRAFANA_URL,
  type DashboardType,
  type GrafanaEmbedParams,
} from '../utils/grafanaEmbed';

interface GrafanaEmbedProps {
  /** Dashboard to display */
  dashboard: DashboardType;
  /** Optional panel ID for single panel embed */
  panelId?: number;
  /** Height of the embed (CSS value) */
  height?: string;
  /** Additional URL parameters */
  params?: GrafanaEmbedParams;
  /** CSS class for the container */
  className?: string;
  /** Whether to sync theme with system preference */
  syncTheme?: boolean;
}

export function GrafanaEmbed({
  dashboard,
  panelId,
  height = '600px',
  params = {},
  className = '',
  syncTheme = true,
}: GrafanaEmbedProps) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(true);
  const [loadingIframe, setLoadingIframe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(getSystemTheme);

  // Track refresh timer
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Listen for system theme changes
  useEffect(() => {
    if (!syncTheme) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [syncTheme]);

  // Fetch signed URL from API
  const fetchSignedUrl = useCallback(async () => {
    try {
      setLoadingUrl(true);
      setError(null);

      const embedParams: GrafanaEmbedParams = {
        ...params,
        theme: syncTheme ? theme : params.theme,
      };

      // For panel embeds, use legacy URL (no signed URL API for panels yet)
      if (panelId !== undefined) {
        const url = getGrafanaPanelUrl(dashboard, panelId, embedParams);
        setEmbedUrl(url);
        setLoadingUrl(false);
        return;
      }

      // Fetch signed URL from API
      const response = await getSignedDashboardUrl(dashboard, embedParams);
      setEmbedUrl(response.url);
      setLoadingUrl(false);

      // Schedule refresh before expiry
      const refreshDelay = getRefreshDelay(response.expires_at);
      if (refreshDelay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          fetchSignedUrl();
        }, refreshDelay);
      }
    } catch (err) {
      console.error('Failed to fetch signed Grafana URL:', err);
      setError('Failed to authenticate for Grafana access');
      setLoadingUrl(false);
    }
  }, [dashboard, panelId, params, syncTheme, theme]);

  // Fetch URL on mount and when dependencies change
  useEffect(() => {
    fetchSignedUrl();

    // Cleanup refresh timer
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [fetchSignedUrl]);

  const handleLoad = () => {
    setLoadingIframe(false);
    setError(null);
  };

  const handleError = () => {
    setLoadingIframe(false);
    setError('Failed to load Grafana dashboard');
  };

  const isLoading = loadingUrl || loadingIframe;

  return (
    <div className={`relative ${className}`} style={{ height }}>
      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {loadingUrl ? 'Authenticating...' : 'Loading Grafana...'}
            </p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 rounded-lg">
          <div className="text-center">
            <p className="text-red-600 mb-2">{error}</p>
            <button
              onClick={() => fetchSignedUrl()}
              className="text-sm text-blue-600 hover:underline mr-4"
            >
              Try again
            </button>
            <a
              href={GRAFANA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:underline"
            >
              Open Grafana directly (admin) →
            </a>
          </div>
        </div>
      )}

      {/* Grafana iframe - only render when we have a URL */}
      {embedUrl && (
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          frameBorder="0"
          onLoad={handleLoad}
          onError={handleError}
          className={`rounded-lg ${isLoading ? 'invisible' : 'visible'}`}
          title={`Grafana ${dashboard} dashboard`}
          allow="fullscreen"
        />
      )}
    </div>
  );
}

export default GrafanaEmbed;
